import React, { useMemo } from 'react';

import {
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Table,
  TableContainer,
  makeStyles,
  useTheme,
} from '@gogame/components';

import CellFormatter from './CellFormatter';
import {
  widthColName,
  widthColMetric,
  widthColTypeIndicator,
} from './constants';
import { getActivityTypeColor } from '../../MarketingPlanningWizard/constants';

const useStyles = makeStyles({
  container: {
    width: '100%',
    overflow: 'auto',
    maxHeight: '500px',
  },
  table: {
    tableLayout: 'fixed',
  },
  nameColumn: {
    width: `${widthColName}px`,
  },
  nameEntityColumn: {
    width: `${4 * widthColMetric}px`,
  },
  metricColumn: {
    width: (props) => `${props.countMetrics * widthColMetric}px`,
    whiteSpace: 'nowrap',
  },
});

export default ({ data, rowsName }) => {
  const theme = useTheme();
  const line1 = useMemo(() => {
    return data.headers.line1.map((name, index) => ({
      name: name,
      colSpan: data.headers.line2?.[index]?.length,
      align: 'left',
    }));
  }, [data.headers.line1, data.headers.line2]);

  const [body, footer] = useMemo(() => {
    const rows = data.rows.map((row, index) => {
      const currentName = `${data.rows?.[index]?.name}`;
      const nextName = `${data.rows?.[index + 1]?.name}`;
      const prevName = `${data.rows?.[index - 1]?.name}`;
      let rowSpan =
        nextName === currentName ? 2 : prevName === currentName ? 0 : 1;
      return { ...row, rowSpan };
    });
    return [
      rows.filter(({ name }) => name !== 'Total'),
      rows.filter(({ name }) => name === 'Total'),
    ];
  }, [data.rows]);

  const countMetrics = data?.headers?.line2?.[0]?.length || 0;

  const classes = useStyles({
    widthColName,
    widthColTypeIndicator,
    countMetrics: countMetrics,
  });

  const handleBorderLeft = React.useCallback(
    (groupIndex, metricIndex = 0) => {
      return groupIndex !== 0 && metricIndex === 0;
    },
    [countMetrics]
  );

  return (
    <TableContainer className={classes.container}>
      <Table
        name={'summaryTable'}
        className={classes.table}
        leftStickyColumnCount={2}
        rightStickyColumnCount={countMetrics}
        stickyHeader={true}
        stickyFooter={true}
        striped={true}
      >
        <TableHead>
          <TableRow borderTop={true}>
            <TableCell className={classes.nameColumn} align={'left'} />
            <TableCell
              width={'4'}
              padding={'none'}
              align={'left'}
              borderRight={true}
            />
            {line1.map((item, i) => (
              <TableCell
                key={i}
                colSpan={item.colSpan}
                className={classes.nameEntityColumn}
                align={'left'}
                borderLeft={handleBorderLeft(i)}
                columnTitle={item.name}
              >
                {item.name}
              </TableCell>
            ))}
          </TableRow>
          <TableRow borderBottom={true}>
            <TableCell className={classes.nameColumn}>{rowsName}</TableCell>
            <TableCell borderRight={true} />
            {data.headers.line2.map((items, i) =>
              items.map((item, j) => (
                <TableCell
                  key={`${i}_${j}`}
                  align={'right'}
                  className={classes.metricColumn}
                  borderLeft={handleBorderLeft(i, j)}
                  label={`summary_head_cell_${i}_${j}`}
                >
                  {item}
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {body.map((row, i) => (
            <TableRow key={i} label={row.name}>
              {row.rowSpan > 0 ? (
                <TableCell
                  label={`${row.name}_${i}`}
                  rowSpan={row.rowSpan}
                  component={'th'}
                  scope={'row'}
                >
                  {row.name}
                </TableCell>
              ) : null}
              <TableCell
                style={{
                  background: getActivityTypeColor(row.activityType, theme),
                }}
              />
              {row.data.map((record, j) =>
                record.map((item, k) => (
                  <TableCell
                    key={`${j}_${k}`}
                    align={'right'}
                    label={line1[k]}
                    borderLeft={handleBorderLeft(j, k)}
                  >
                    <CellFormatter
                      type={item.type}
                      value={item.value}
                      precision={item.precision}
                    />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          {footer.map((row, i) => (
            <TableRow key={i} borderTop={true}>
              {row.rowSpan > 0 ? (
                <TableCell
                  label={`footer_name_${row.name}_${i}`}
                  rowSpan={row.rowSpan}
                  component={'th'}
                  scope={'row'}
                  variant={'footer'}
                  colSpan={2}
                  borderRight={true}
                >
                  {row.name}
                </TableCell>
              ) : null}

              {row.data.map((record, i) =>
                record.map((item, j) => (
                  <TableCell
                    key={`${i}_${j}`}
                    align={'right'}
                    variant={'footer'}
                    borderLeft={handleBorderLeft(i, j)}
                    label={`footer_${i}_${j}`}
                  >
                    <CellFormatter
                      type={item.type}
                      value={item.value}
                      precision={item.precision}
                    />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableFooter>
      </Table>
    </TableContainer>
  );
};
